import React, { Component } from "react"
import Api from "./Http/Api"
import Years from "./Pages/categories/Years"
import Year from "./Pages/photos/Year"
import Favorites from "./Pages/photos/Favorites"
import Collections from "./Pages/categories/Collections"
import Events from "./Pages/categories/Events"
import Event from "./Pages/photos/Event"
import LatestUploads from "./Pages/photos/LatestUploads"
import PhotoOrganiserNavigation from "./Components/PhotoOrganiserNavigation"
import { SharedContext, SharedContextType, SelectionManager } from "@albelli/photoselector"
import "./styles/photoorganiser.css"
import "./styles/Title.css"
import "./styles/ImageCount.css"
import "./styles/Navigate.css"
import "./styles/Calendar.css"
import "./styles/Similars.css"

export enum PhotoOrganiserPages {
  Years,
  Year,
  Collections,
  Collection,
  Favorites,
  Events,
  Event,
  LatestUploads
}

export interface PhotoOrganiserState {
  currentPhotoOrganiserPage: PhotoOrganiserPages
  currentSinglePageId: string
  photoCount: number
  collectionsSharedWithMe: number
}

export interface PhotoOrganiserProps {
  queryApi: string
  authenticator: () => Promise<string>
}

interface PhotoOrganiserContentProps {
  children: React.ReactChild
  currentPage: PhotoOrganiserPages
  navigateTo: (page: PhotoOrganiserPages) => void
}
// Component that wrappes context with Navigation
// considering PhotoOrganiser component quite big in size, decided to extract wrapper for context
// to this component to have better readability
const PhotoOrganiserContent: React.FunctionComponent<PhotoOrganiserContentProps> = ({ currentPage, navigateTo, children }) => {
  return (
    <>
      <PhotoOrganiserNavigation currentPage={currentPage} navigateTo={navigateTo} />
      <div className="photoselector-page-container">{children}</div>
    </>
  )
}

export class PhotoOrganiser extends Component<PhotoOrganiserProps, PhotoOrganiserState> {
  static contextType = SharedContext
  private api!: Api

  state = {
    currentPhotoOrganiserPage: PhotoOrganiserPages.Years,
    currentSinglePageId: "",
    collectionsSharedWithMe: 0,
    photoCount: 0
  }

  componentDidMount() {
    this.api = new Api(this.props.queryApi, this.props.authenticator)
    this.api
      .getStats()
      .then((response) => {
        const { PhotoCount, CollectionsSharedWithMe } = response.stats
        this.setState({ photoCount: PhotoCount, collectionsSharedWithMe: CollectionsSharedWithMe })
        // If I don't have any photos of my own but have shared collections, then set initial page
        // as collections
        if (PhotoCount === 0 && CollectionsSharedWithMe > 0) {
          this.setState({ currentPhotoOrganiserPage: PhotoOrganiserPages.Collections })
        }
        return
      })
      .catch((e) => console.error("Unable to retrieve PhotoOrganiser stats.", e))
  }

  navigateToPageById(currentPhotoOrganiserPage: PhotoOrganiserPages) {
    return (currentSinglePageId: string) => {
      this.setState({ currentPhotoOrganiserPage, currentSinglePageId })
    }
  }

  navigateTo(currentPhotoOrganiserPage: PhotoOrganiserPages) {
    this.setState({ currentPhotoOrganiserPage })
  }

  getCurrentPage(store: SelectionManager) {
    const { currentSinglePageId, currentPhotoOrganiserPage } = this.state

    switch (currentPhotoOrganiserPage) {
      case PhotoOrganiserPages.Years:
        return <Years navigateTo={this.navigateToPageById(PhotoOrganiserPages.Year)} request={this.api.getYears.bind(this.api)} />

      case PhotoOrganiserPages.Events:
        return (
          <Events
            navigateTo={this.navigateToPageById(PhotoOrganiserPages.Event)}
            request={this.api.getEvents.bind(this.api)}
            sortingDirection="Descending"
          />
        )

      case PhotoOrganiserPages.Collections:
        return (
          <Collections
            navigateTo={this.navigateToPageById(PhotoOrganiserPages.Collection)}
            request={this.api.getCollections.bind(this.api)}
            sortingDirection="Descending"
            SortingField="CollectionCreateDate"
          />
        )

      case PhotoOrganiserPages.Year:
        return (
          <Year
            navigateTo={() => this.navigateTo(PhotoOrganiserPages.Years)}
            yearId={currentSinglePageId}
            store={store}
            request={this.api.getYear(currentSinglePageId).bind(this.api)}
            getSimilars={this.api.getSimilarPhotos.bind(this.api)}
          />
        )

      case PhotoOrganiserPages.LatestUploads:
        return (
          <LatestUploads store={store} request={this.api.getLatestUploads.bind(this.api)} getSimilars={this.api.getSimilarPhotos.bind(this.api)} />
        )

      case PhotoOrganiserPages.Favorites:
        return <Favorites store={store} request={this.api.getFavorites.bind(this.api)} sortingDirection="Descending" />

      case PhotoOrganiserPages.Collection:
        return (
          <Event
            navigateTo={() => this.navigateTo(PhotoOrganiserPages.Collections)}
            collectionId={currentSinglePageId}
            store={store}
            request={this.api.getCollection(currentSinglePageId).bind(this.api)}
          />
        )

      case PhotoOrganiserPages.Event:
        return (
          <Event
            navigateTo={() => this.navigateTo(PhotoOrganiserPages.Events)}
            store={store}
            request={this.api.getEvent(currentSinglePageId).bind(this.api)}
            getSimilars={this.api.getSimilarPhotos.bind(this.api)}
          />
        )
    }
  }

  render() {
    const { currentPhotoOrganiserPage, photoCount, collectionsSharedWithMe } = this.state
    const { selection }: SharedContextType = this.context
    return (
      <div className="photoselector-photoorganiser-app">
        {photoCount > 0 || collectionsSharedWithMe > 0 ? (
          <PhotoOrganiserContent currentPage={currentPhotoOrganiserPage} navigateTo={this.navigateTo.bind(this)}>
            {this.getCurrentPage(selection)}
          </PhotoOrganiserContent>
        ) : (
          <div className="photoselector-photoorganiser-app-empty-account">{this.props.children}</div>
        )}
      </div>
    )
  }
}
